import type { IShippingInfo } from 'pages/Dashboard/Onboard/Onboard';

/** The form's snake_case address as the device-order API spells it. */
export const toDeviceOrderShippingAddress = (address: IAddress): IDeviceOrderShippingAddress => ({
  streetAddressLine1: address.street_address_line_1,
  streetAddressLine2: address.street_address_line_2,
  city: address.city,
  stateProvince: address.state_province,
  postalCode: address.postal_code,
  countryCode: address.country_code || 'US'
});

/**
 * The shipping block every device-order call sends: the order itself, its quote, the shipping
 * options preview and a replacement. Built in one place so a preview is never requested for a
 * slightly different address than the order is placed with.
 */
export const toDeviceOrderShippingInfo = (
  shippingInfo: IShippingInfo
): IDeviceOrderShippingInfo => ({
  firstName: shippingInfo.firstName,
  lastName: shippingInfo.lastName,
  email: shippingInfo.email,
  phoneNumber: shippingInfo.phoneNumber,
  address: toDeviceOrderShippingAddress(shippingInfo.address)
});

export const addressFormatterFromGMaps = (place: google.maps.GeocoderResult): IAddress => {
  const street_number = place.address_components.find((x) =>
    x.types.includes('street_number')
  )?.long_name;
  const route = place.address_components.find((x) => x.types.includes('route'))?.long_name;
  return {
    city:
      place.address_components.find((x) => x.types.includes('locality'))?.long_name ||
      place.address_components.find((x) => x.types.includes('postal_town'))?.long_name ||
      place.address_components.find((x) => x.types.includes('sublocality'))?.long_name ||
      '',
    country_code:
      place.address_components.find((x) => x.types.includes('country'))?.short_name || '',
    state_province:
      place.address_components.find((x) => x.types.includes('administrative_area_level_1'))
        ?.long_name || '',
    street_address_line_1: `${street_number ? street_number + ' ' : ''}${route}`,
    postal_code:
      place.address_components.find((x) => x.types.includes('postal_code'))?.long_name || ''
  };
};

export const isAddressValid = (address: IAddress | null | undefined) => {
  return (
    !isAddressEmpty(address) &&
    address?.street_address_line_1 &&
    address?.city &&
    address?.state_province &&
    address?.postal_code
  );
};

export const isAddressEmpty = (address: IAddress | null | undefined) => {
  if (address) {
    for (const key of Object.keys(address)) {
      if (address[key] !== '') return false;
    }
    return true;
  } else return false;
};

export const addressFormatterInline = (address: IAddress | null, showApt = false): string => {
  if (!address || isAddressEmpty(address)) {
    return '';
  } else {
    return `${
      showApt && address.street_address_line_2 ? address.street_address_line_2 + ' -' : ''
    } ${address.street_address_line_1}, ${address.city}, ${address.state_province} ${
      address.postal_code
    }, ${address.country_code}`;
  }
};

export const addressesAreEqual = (a?: IAddress | null, b?: IAddress | null): boolean => {
  const fields: (keyof IAddress)[] = [
    'street_address_line_1',
    'street_address_line_2',
    'city',
    'state_province',
    'postal_code',
    'country_code'
  ];
  return fields.every(
    (field) =>
      String(a?.[field] || '')
        .trim()
        .toLowerCase() ===
      String(b?.[field] || '')
        .trim()
        .toLowerCase()
  );
};

export const getDefaultAddress = (address?: IAddress | null) => {
  return {
    address_id: address?.address_id || '',
    city: address?.city || '',
    country_code: address?.country_code || '',
    state_province: address?.state_province || '',
    street_address_line_1: address?.street_address_line_1 || '',
    street_address_line_2: address?.street_address_line_2 || '',
    postal_code: address?.postal_code || ''
  };
};

export const shippingAddressToForm = (
  address?: Partial<IDeviceOrderShippingAddress> | null
): IAddress =>
  getDefaultAddress({
    street_address_line_1: address?.streetAddressLine1 || '',
    street_address_line_2: address?.streetAddressLine2 || '',
    city: address?.city || '',
    state_province: address?.stateProvince || '',
    postal_code: address?.postalCode || '',
    country_code: address?.countryCode || ''
  });
